/*
 * @format
 */

import React from 'react';
import { ClipPath, Defs, G, Path, Rect, Text } from 'react-native-svg';

import {
  DiagramFrame,
  Label,
  Leader,
  MEDIUM,
  REGULAR,
  stopShortOf,
  TapDot,
  TAP_RADIUS,
} from './DiagramParts';
import { modeIconPath } from '../../components/common/iconPaths';
import { accent, base, black, primary, white } from '../../colours';

/*
 * The mode picker — the screen a tap on the mode pill opens. Its contents come
 * from ModeEditor.kt and Modes.kt: the clock, a "Mode" header, and a row per
 * mode, each led by the phone's own icon for it, with the zone's current mode
 * filled in accent and ticked.
 *
 * The screen is drawn taller than it is wide rather than round, as the design
 * does: four rows plus a header will not fit a round face without the last of
 * them being clipped down to a sliver, and this diagram's whole job is to show
 * the reader which row to reach for.
 */
const WIDTH = 360;
// The screen alone sets the height: the caption sits beside it rather than
// under it, so the frame ends a margin below the screen instead of carrying a
// band of empty space for a line of text.
const HEIGHT = 436;
const CX = WIDTH / 2;

/*
 * The screen is sized around the list rather than the other way about: its
 * straight section has to be long enough to hold all four rows, so the last of
 * them and the fingertip both sit clear of the bottom curve.
 */
const SCREEN = { width: 200, height: 400, top: 14 };
const SCREEN_LEFT = CX - SCREEN.width / 2;

// The order modeOptionsFor() builds for a heating zone: Plus Hours is third,
// above Off — not last, as it is easy to assume.
const MODES = [
  { label: 'Auto', icon: modeIconPath.auto },
  { label: 'Manual', icon: modeIconPath.manual },
  { label: 'Plus Hours', icon: modeIconPath.plusHours },
  { label: 'Off', icon: modeIconPath.off },
];

// The picker fills in the mode the zone is ALREADY on — the same Auto the zone
// screen shows — not the one being reached for. Plus Hours is never filled in:
// isHighlightedMode() excludes it, so an active Plus Hours leaves every row
// plain.
const CURRENT = 'Auto';

const CLOCK_Y = 76;
const HEADER_Y = 116;

const ROW_WIDTH = 176;
const ROW_HEIGHT = 44;
const ROW_GAP = 8;
const ROW_TOP = 148;
const ROW_INSET = 16;
const ROW_ICON = 20;
// The glyphs are drawn in a 24-unit box, as the phone's own icons are.
const ROW_ICON_SCALE = ROW_ICON / 24;
const ROW_LABEL_GAP = 10;
const ROW_LEFT = CX - ROW_WIDTH / 2;

const rowTop = index => ROW_TOP + index * (ROW_HEIGHT + ROW_GAP);

// Toward the row's trailing edge, clear of the longest label.
const tapFor = index => ({
  x: CX + ROW_WIDTH / 2 - 16,
  y: rowTop(index) + ROW_HEIGHT / 2,
});

/*
 * The caption sits in the gutter beside the list with a line reaching down to
 * the fingertip, rather than centred below it: centred, it reads as a title for
 * the whole picture instead of naming the one row to press.
 *
 * It breaks over two lines to fit the gutter's width, and both it and the point
 * the leader leaves from are measured up from the fingertip rather than fixed to
 * the frame, so the caption rides with whichever row the topic is pointing at
 * and the line always arrives from above it.
 */
const CAPTION_X = WIDTH - 6;
const CAPTION_RISE = 78;
const LEADER_FROM_X = 312;
const LEADER_FROM_RISE = 42;
/*
 * The line arrives from above the fingertip, so the shadow beneath it is not in
 * the way and the disc's own edge is what has to be cleared. The clearance
 * carries the dot on the leader's end as well as the gap after it.
 */
const LEADER_CLEARANCE = 10;

function ModeRow({ label, icon, index }) {
  const filled = label === CURRENT;
  const ink = filled ? base(900) : white;
  const top = rowTop(index);
  const middle = top + ROW_HEIGHT / 2;

  return (
    <G>
      <Rect
        x={ROW_LEFT}
        y={top}
        width={ROW_WIDTH}
        height={ROW_HEIGHT}
        rx={ROW_HEIGHT / 2}
        fill={filled ? accent(400) : primary(900)}
      />
      {/* The phone's own mode glyph, scaled into place. Drawn as a path rather
          than by nesting the icon component's own <Svg>: a nested viewport
          stops the siblings after it from rendering. */}
      <G
        transform={`translate(${ROW_LEFT + ROW_INSET} ${
          middle - ROW_ICON / 2
        }) scale(${ROW_ICON_SCALE})`}>
        <Path d={icon} fill={filled ? base(900) : accent(200)} />
      </G>
      <Text
        x={ROW_LEFT + ROW_INSET + ROW_ICON + ROW_LABEL_GAP}
        y={middle + 6}
        fill={ink}
        fontFamily={REGULAR}
        fontSize={17}>
        {label}
      </Text>
      {filled ? (
        <Text
          x={ROW_LEFT + ROW_WIDTH - ROW_INSET}
          y={middle + 6}
          fill={ink}
          fontFamily={REGULAR}
          fontSize={17}
          textAnchor="end">
          ✓
        </Text>
      ) : null}
    </G>
  );
}

/*
 * [target] is the mode this topic is telling the reader to press: the row the
 * fingertip and its caption are put on. Every topic that shows the picker is
 * teaching the reader to choose some mode, but not the same one, so the row is
 * the caller's to name.
 */
function WatchModeListDiagram({ target }) {
  const targetIndex = MODES.findIndex(mode => mode.label === target);

  /*
   * A row that is not on the picker is a caller's mistake, and a silent one:
   * findIndex answers -1, which lands the fingertip a row ABOVE the first —
   * on the "Mode" header — and captions the illustration "fingertip on
   * undefined" for anyone listening to it rather than looking at it.
   */
  if (targetIndex < 0) {
    throw new Error(
      `WatchModeListDiagram: no "${target}" row on the mode picker. ` +
        `Expected one of: ${MODES.map(mode => mode.label).join(', ')}.`,
    );
  }

  const tap = tapFor(targetIndex);
  const caption = { x: CAPTION_X, y: tap.y - CAPTION_RISE };
  const leaderFrom = { x: LEADER_FROM_X, y: tap.y - LEADER_FROM_RISE };
  const captionAnchor = stopShortOf(
    leaderFrom,
    tap,
    TAP_RADIUS + LEADER_CLEARANCE,
  );

  return (
    <DiagramFrame
      width={WIDTH}
      height={HEIGHT}
      description={
        'The watch mode picker, listing Auto, Manual, Plus Hours and Off, ' +
        'with Auto ticked as the current mode. A line points to a fingertip ' +
        `on ${target}.`
      }>
      <Defs>
        <ClipPath id="mode-list-screen">
          <Rect
            x={SCREEN_LEFT}
            y={SCREEN.top}
            width={SCREEN.width}
            height={SCREEN.height}
            rx={SCREEN.width / 2}
          />
        </ClipPath>
      </Defs>

      <Rect
        x={SCREEN_LEFT}
        y={SCREEN.top}
        width={SCREEN.width}
        height={SCREEN.height}
        rx={SCREEN.width / 2}
        fill={black}
      />

      <G clipPath="url(#mode-list-screen)">
        <Text
          x={CX}
          y={CLOCK_Y}
          fill={white}
          fontFamily={MEDIUM}
          fontSize={15}
          textAnchor="middle">
          9:30
        </Text>
        <Text
          x={CX}
          y={HEADER_Y}
          fill={white}
          fontFamily={MEDIUM}
          fontSize={17}
          textAnchor="middle">
          Mode
        </Text>

        {MODES.map((mode, index) => (
          <ModeRow key={mode.label} index={index} {...mode} />
        ))}
      </G>

      <TapDot x={tap.x} y={tap.y} />

      <Leader from={leaderFrom} to={captionAnchor} />
      <Label
        x={caption.x}
        y={caption.y}
        anchor="end"
        bold
        heading={['Tap to', 'select']}
      />
    </DiagramFrame>
  );
}

export default WatchModeListDiagram;
